import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { getRequests } from './api';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import './index.css';

// The login screen is loaded immediately. Operational screens are loaded only
// when opened, so visitors do not download charts, reports, and admin tools
// before authentication.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Procurement = lazy(() => import('./pages/Procurement'));
const Issues = lazy(() => import('./pages/Issues'));
const Requests = lazy(() => import('./pages/Requests'));
const Quotations = lazy(() => import('./pages/Quotations'));
const Reports = lazy(() => import('./pages/Reports'));
const ReceiverReport = lazy(() => import('./pages/ReceiverReport'));
const Users = lazy(() => import('./pages/Users'));
const ActivityLog = lazy(() => import('./pages/ActivityLog'));
const Projects = lazy(() => import('./pages/Projects'));
const SetPassword = lazy(() => import('./pages/SetPassword'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const Profile = lazy(() => import('./pages/Profile'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Settings = lazy(() => import('./pages/Settings'));

function PageLoader() {
  return <div className="page-loading" role="status" aria-live="polite" aria-label="Loading page"><div className="spinner" /></div>;
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loading"><div className="spinner"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && window.location.pathname !== '/change-password') return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/projects" replace />;
  return children;
}

function AppLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const currentProjectId = location.pathname.match(/^\/projects\/([^/]+)/)?.[1];

  useEffect(() => {
    if (user && currentProjectId) {
      getRequests({ status: 'pending', project_id: currentProjectId }).then(r => setPendingCount(r.data.length)).catch(() => {});
      const interval = setInterval(() => {
        getRequests({ status: 'pending', project_id: currentProjectId }).then(r => setPendingCount(r.data.length)).catch(() => {});
      }, 30000);
      return () => clearInterval(interval);
    }
    setPendingCount(0);
  }, [user, currentProjectId]);

  return (
    <div className={`app-layout ${sidebarOpen ? 'sidebar-visible' : ''}`}>
      <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Open navigation">
        <span></span>
        <span></span>
        <span></span>
      </button>
      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
      <Sidebar pendingCount={pendingCount} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className="main-content" id="main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/" element={<Navigate to="/projects" replace />} />
          <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
          <Route path="/projects/:projectId" element={<Navigate to="dashboard" replace />} />
          <Route path="/projects/:projectId/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/projects/:projectId/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
          <Route path="/projects/:projectId/procurement" element={<ProtectedRoute roles={['admin','sub_admin','store_manager']}><Procurement /></ProtectedRoute>} />
          <Route path="/projects/:projectId/issues" element={<ProtectedRoute roles={['admin','sub_admin','store_manager']}><Issues /></ProtectedRoute>} />
          <Route path="/projects/:projectId/requests" element={<ProtectedRoute><Requests /></ProtectedRoute>} />
          <Route path="/projects/:projectId/quotations" element={<ProtectedRoute><Quotations /></ProtectedRoute>} />
          <Route path="/projects/:projectId/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path="/projects/:projectId/receiver-report" element={<ProtectedRoute><ReceiverReport /></ProtectedRoute>} />
          <Route path="/activity" element={<ProtectedRoute roles={['admin']}><ActivityLog /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute roles={['admin']}><Users /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute roles={['admin']}><Settings /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/projects" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

export default function App() {
  useEffect(() => {
    document.body.classList.toggle('theme-dark', localStorage.getItem('theme') === 'dark');
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" toastOptions={{ duration: 3000, style: { fontSize: '13px' } }} />
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-password/:token" element={<SetPassword />} />
          <Route path="/*" element={<AppLayout />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loading"><div className="spinner"></div></div>;
  if (user) return <Navigate to={user.must_change_password ? '/change-password' : '/projects'} replace />;
  return children;
}
